import React, {useState, useEffect} from 'react';
import {createRoot} from 'react-dom/client';
import './modal.css';
import {
    reqBaseData,
    Role,
    Industry,
    Question,
    ChildQuestion,
    saveData,
    saveLog,
    submitData,
    lingxiSubmitData
} from './api';
import QRCodeRenderer from './QRCodeRenderer';
// 定义弹窗参数类型
export interface PopupParams {
    [key: string]: any;
}
// 弹窗根组件 - 包含所有弹窗状态
const PopupRoot: React.FC = () => {
    const [step, setStep] = useState(1);
    const [isBack, setIsBack] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [params, setParams] = useState<PopupParams>({});
    const [resultData, setResultData] = useState<PopupParams>({});
    const [selectedRole, setSelectedRole] = useState<any>({});
    const [selectedIndustry, setSelectedIndustry] = useState<string>('');
    const [selectedBusinessType, setSelectedBusinessType] = useState<string>('商贸业务'); // 新增：业务类型选择，默认为商贸业务
    const [selectedIssues, setSelectedIssues] = useState<any[]>([]);
    const [validationMessage, setValidationMessage] = useState<string | null>(null); // 新增：验证提示消息状态
    const [roles, setRoles] = useState<Role[]>([]);
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(false); // 修改：默认不加载
    const [error, setError] = useState<string | null>(null); // 新增：错误状态
    const [submitting, setSubmitting] = useState(false); // 新增：提交状态
    const [showEmptyResultLoading, setShowEmptyResultLoading] = useState(false); // 新增：当uuid和accountUrl都为空时显示的loading状态

    // 获取基础数据的函数
    const fetchData = async () => {

        try {
            setLoading(true);
            setError(null);
            const response = await reqBaseData() as any;
            if (response.code === 0) {
                setRoles(response.data.roleList);
                setSelectedRole(response.data.roleList[1])
                setIndustries(response.data.industryList);
                setQuestions(response.data.questionList);

                // 设置默认业务类型为第一个可用的业务类型
                if (response.data.questionList.length > 0) {
                    setSelectedBusinessType(response.data.questionList[2].questionName);
                }
            } else {
                setError(response.message || '获取数据失败');
            }
        } catch (err) {
            setError('请求失败: ' + (err as Error).message);
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };
    const refreshData = () => {
        setSelectedRole({});
        setSelectedIndustry('');
        setSelectedIssues([]);
        setIsBack(false);

        // 重置到第1步并隐藏loading状态
        setStep(1);
        // initQRCode('wl_id')

    }
    // 监听全局函数调用
    useEffect(() => {
        // 将控制函数直接挂载到 window.PopupAPI，方便外部调用
        (window as any).PopupAPI = {
            open: (param: PopupParams) => {
                setIsOpen(true);
                setParams({...param});
                refreshData()
                // 只有在弹窗打开时才获取数据
                if (roles.length === 0 && questions.length === 0 && industries.length === 0) {
                    fetchData();
                }
            },
            close: () => {
                setIsOpen(false);
                const {isLingxi} = params
                if(step == 1 && !isBack && !isLingxi){
                    submitFn()
                }
            }
        };

        // 组件卸载时清理全局引用
        return () => {
            (window as any).PopupAPI = null;
        };
    }, []); // 依赖数组为空，避免重复绑定

    const closeModal = () => {
        setIsOpen(false);
        const {isLingxi} = params
        if(step == 1 && !isBack && !isLingxi){
            submitFn()
        }
    }
    // 处理角色选择
    const handleRoleSelect = (role: any) => {
        setSelectedRole(role);
    };

    // 处理行业选择
    const handleIndustryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedIndustry(e.target.value);
    };

    // 处理业务类型选择
    const handleBusinessTypeSelect = (type: string) => {

        setSelectedBusinessType(type);
        // 切换业务类型时清空已选问题
        setSelectedIssues([]);
    };

    // 处理问题选项选择
    const handleIssueSelect = (issues: any) => {
        // 根据当前选中的业务类型获取最大选择数
        const currentQuestion = questions.find(q => q.questionName === selectedBusinessType);
        const maxSelections = currentQuestion ? currentQuestion.num : 3;
        const ids = selectedIssues.map(item => item.id) || []
        if (ids.includes(issues.id)) {
            // 如果已选中则取消选择
            setSelectedIssues(selectedIssues.filter(item => item.id !== issues.id));
        } else if (selectedIssues.length < maxSelections) {
            // 如果未选中且未达到最大选择数，则添加选择
            setSelectedIssues([...selectedIssues, issues]);
        }
    };

    // 获取当前业务类型的最大选择数
    const getMaxSelections = () => {
        const currentQuestion = questions.find(q => q.questionName === selectedBusinessType);
        return currentQuestion ? currentQuestion.num : 3;
    };

    // 获取当前业务类型的问题列表
    const getCurrentQuestionChildren = () => {
        const currentQuestion = questions.find(q => q.questionName === selectedBusinessType);
        return currentQuestion ? currentQuestion.childQuestions : [];
    };
    const submitFn = async (resData = {uuid: ''},isClose = true) => {
        const param = params.params
        if(isClose){
            await submitData(param)
        } else {
            const attentionQuestion = selectedIssues.map(item => item.childQuestionName) || [];
            const customJsonVoList = [
                {
                    fieldCode: 'zdy_100003_15',
                    fieldValue: selectedRole.roleName
                },
                {
                    fieldCode: 'zdy_100003_16',
                    fieldValue: `行业：${selectedIndustry},业态：${selectedIssues[0].questionName},关注问题:${attentionQuestion.join(',')},地址:https://www.chanjetai.com/semai/solution/${resData.uuid}.html`,
                },

            ]
            const {isLingxi,partnerCode,c,channel,phone} = params
            if(isLingxi){
                await lingxiSubmitData({
                    agent_code: partnerCode,
                    cluePartnerCode: partnerCode,
                    clueIntentionalProduct: param.clueIntentionalProduct,
                    mobile: phone,
                    product_id: param.product_id,
                    c: c,
                    channel,
                    businessFormat: 'sem-表单',
                    utmUserSearch: '灵犀页面',
                    source: "semThousandFace",
                    clueSecondarySource: "youzhiform",
                    clueSecondarySourceD: "SEM千词千面",
                    channelSourceLevel2: "youzhiform",
                    url: location.href,
                    phone: phone,
                    email: '',
                    industry: '',
                    sceneId: 'ca51abvo',
                    type: 1,
                    customJsonVoList
                })
            } else {
                param.customJsonVoList = customJsonVoList
                await submitData(param)
            }
        }
    };
    const warning = (message: string) => {
        setValidationMessage(message);
        // 3秒后自动清除提示
        setTimeout(() => {
            setValidationMessage(null);
        }, 3000);
    };
    // 验证表单
    const validateForm = () => {
        if (!selectedRole.id) {
            warning('请选择您的角色')
            return false;
        }
        if (!selectedIndustry) {
            warning('请选择您所在的行业')
            return false;
        }
        if (!selectedBusinessType) {
            warning('请选择业务类型')
            return false;
        }
        if (selectedIssues.length === 0) {
            warning(`${selectedBusinessType}至少需要选择一个问题`)
            return false;
        }
        return true;
    };

    const initQRCode = (elId: string) => {
        if (window.wlQrcodeNostyleComp) {
            window.wlQrcodeNostyleComp.init({
                wl_id: "ww92653858095c7bd4",
                wl_qrcode_id: ['2000818930188582912'],
                DOMid: [elId],
            });
        }
    }
    // 处理提交
    const stepChange = async (step: number) => {
        if (step === 2) {
            // 验证表单
            if (!validateForm()) {
                return; // 如果验证失败，不进入下一步
            }

            // 检查是否已经在提交过程中
            if (submitting) {
                return; // 如果正在提交，则不执行任何操作
            }

            // 设置提交状态，禁用按钮
            setSubmitting(true);

            try {
                // 这里可以调用提交接口，使用现有接口作为示例
                // 实际项目中应该使用专门的提交接口
                // await submitForm(); // 假设这是提交表单的函数
                const {code, phone, sourceId, partnerCode, c, channel} = params
                const industryId = industries.find(item => item.industryName == selectedIndustry)?.id
                const param = {
                    phone,
                    industryId,
                    industryName: selectedIndustry,
                    roleId: selectedRole.id,
                    roleName: selectedRole.roleName,
                    questions: selectedIssues,
                    sourceId: sourceId || location.pathname,
                    sourceType: 'sem_industry',
                    partnerCode,
                    c,
                    channel
                } as any;
                // if (!isBack) {
                //     param.code = code
                // }
                // submitFn({uuid:'000'}, false)
                // return
                const resData = await saveData(param) as any; // 模拟API请求
                if (resData.code === 0) {
                    // 请求完成后恢复按钮状态
                    setResultData(resData.data || {})

                    // 检查 uuid 和 accountUrl 是否都为空
                    const hasUuid = resData.data && resData.data.uuid;
                    const hasAccountUrl = resData.data && resData.data.accountUrl;
                    setStep(step);
                    initQRCode('wl_id')
                    submitFn(resData.data, false)
                    if (!hasUuid && !hasAccountUrl) {
                        // 如果两个字段都为空，显示loading状态
                        setShowEmptyResultLoading(true);
                        // 30秒后清空数据并提示用户重新填写
                        setTimeout(() => {
                            // 清空已选择的数据
                            refreshData()
                            setShowEmptyResultLoading(false);
                            // 提示用户重新填写
                            warning('未匹配到合适的解决方案，请重新填写信息');
                        }, 30 * 1000); // 30秒后执行
                    }

                } else {
                    warning('请关闭弹窗重新提交或稍后尝试～')
                }
                setSubmitting(false);

            } catch (error) {
                console.error('提交失败:', error);
            }
        } else {
            setStep(step);
            setIsBack(true)
        }
    };
    const recordLog = async (logType: number) => {
        const {phone} = params
        const {uuid, solutionsId} = resultData

        const param = {
            phone,
            customerQuestionUuid: uuid,
            logType,
            solutionsId: solutionsId
        }
        await saveLog(param)
    }
    const goSolution = () => {
        recordLog(3)
        const id = resultData.uuid
        window.open(`https://www.chanjetai.com/semai/solution/${id}.html`, '_blank');
    };
    const goAccount = () => {
        recordLog(2)
        const url = resultData.accountUrl
        window.open(url, '_blank');
    }

    // 渲染角色选择组件
    const renderRoleOptions = () => {
        if (loading) {
            return <p>加载中...</p>;
        }
        if (error) {
            return <p>错误: {error}</p>;
        }
        if (roles.length === 0) {
            return <p>暂无数据</p>;
        }
        return roles.map((role) => (
            <button
                key={role.id}
                className={`role-option ${selectedRole.roleName === role.roleName ? 'selected' : ''}`}
                onClick={() => handleRoleSelect(role)}
            >
                {role.roleName}
            </button>
        ));
    };

    // 渲染业务类型标签页
    const renderBusinessTypeTabs = () => {
        if (questions.length === 0) {
            return <p>暂无数据</p>;
        }
        return questions.map((question) => (
            <button
                key={question.id}
                className={`tab-option ${selectedBusinessType === question.questionName ? 'selected' : ''}`}
                onClick={() => handleBusinessTypeSelect(question.questionName)}
            >
                {question.questionName}
            </button>
        ));
    };

    // 渲染问题复选框
    const renderIssueCheckboxes = () => {
        if (questions.length === 0) {
            return <p>暂无数据</p>;
        }
        const currentChildren = getCurrentQuestionChildren();
        return currentChildren.map((childQuestion) => (
            <label key={childQuestion.id} className="checkbox-label">
                <input
                    type="checkbox"
                    checked={selectedIssues.map(item => item.id).includes(childQuestion.id)}
                    onChange={() => handleIssueSelect(childQuestion)}
                />
                <span className="checkbox-text">{childQuestion.childQuestionName}</span>
            </label>
        ));
    };

    // 修改初始加载状态的渲染逻辑
    if (isOpen && loading && roles.length === 0 && questions.length === 0 && industries.length === 0) {
        return (
            <div style={{display: isOpen ? 'block' : 'none'}}>
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="model_content" onClick={(e) => e.stopPropagation()}>
                        <div className="form-container">
                            <div className="loading-section">
                                <p>正在加载数据...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isOpen && error) {
        return (
            <div style={{display: isOpen ? 'block' : 'none'}}>
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="model_content" onClick={(e) => e.stopPropagation()}>
                        <div className="form-container">
                            <div className="error-section">
                                <p>加载数据失败: {error}</p>
                                <button onClick={() => fetchData()}>重新加载</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{display: isOpen ? 'block' : 'none'}}>
            <div className="modal-overlay" onClick={closeModal}>
                <div className="model_content" onClick={(e) => e.stopPropagation()}>
                    <img
                        src="https://service.static.chanjet.com/assets/sem/static/sem/image/close.png"
                        className="close_icon"
                        onClick={closeModal}
                        alt="关闭"
                        width="10px"
                    />
                    <div className="form-container">
                        <div className="header_section">
                            <h2 className="main-title">
                                已收到您的电话 | 补充3步生成 <span className="highlight">专属解决方案</span>
                            </h2>
                            <p className="subtitle">
                                高效.省心.安全-用友畅捷通智能云.财税/进销存/业财一体化
                            </p>
                        </div>
                        <div className="body_section">
                            {step === 1 && (
                                <>
                                    <div className="form-layout-container">
                                        <div className="form-left-section">
                                            <div className="form-steps m_b0">
                                                <div className="step">
                                                    <span className="step-number">01</span>
                                                    <h3 className="step-title">您的角色</h3>
                                                    <div className="role-options">
                                                        {renderRoleOptions()}
                                                    </div>
                                                </div>

                                                <div className="step">
                                                    <span className="step-number">02</span>
                                                    <h3 className="step-title">您所在行业</h3>
                                                    <select
                                                        className="industry-select"
                                                        value={selectedIndustry}
                                                        onChange={handleIndustryChange}
                                                    >
                                                        <option value="">请选择您的行业</option>
                                                        {industries.map((industry) => (
                                                            <option key={industry.id} value={industry.industryName}>
                                                                {industry.industryName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="step">
                                                    <span className="step-number">03</span>
                                                    <h3 className="step-title">
                                                        您最关注的问题：（最多选择{getMaxSelections()}个）
                                                    </h3>
                                                    <div className="business-type-tabs">
                                                        {renderBusinessTypeTabs()}
                                                    </div>

                                                    <div className="issue-checkboxes">
                                                        {renderIssueCheckboxes()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="qrcode-right-section">
                                            <div className="qrcode-top-text">
                                                免费获取您的<span className="red-text">专属解决方案</span> <br/><span
                                                className="red-text">扫码添加</span>行业顾问
                                            </div>
                                            <div className="qrcode-image-container">
                                                <QRCodeRenderer
                                                    domId="qrcode_image_container"
                                                    wlId="2044242698226987008"
                                                    wl_id="ww92653858095c7bd4"
                                                />
                                            </div>
                                            <div className="qrcode-benefit-section">
                                                <div className="benefit-item" style={{marginBottom: '5px'}}>
                                                    <span className="red-arrow">▼</span> 您将获得 <span
                                                    className="red-arrow">▼</span>
                                                </div>
                                                <div className="benefit-item bold">
                                                    1对1免费<span className="red-text">业务诊断</span>
                                                </div>
                                                <div className="benefit-item bold">
                                                    针对您的行业<span className="red-text">量身定制</span>
                                                </div>
                                                <div className="benefit-item bold">
                                                    同行业<span className="red-text">成功案例</span>参考
                                                </div>
                                            </div>

                                            {/*<div className="qrcode-decoration-container">*/}
                                            {/*    <div className="qrcode-decoration-left">*/}
                                            {/*        <div className="decoration-text">为了快速帮您服务</div>*/}
                                            {/*        <div className="decoration-text">*/}
                                            {/*            添加二维码时请<span className="red-text">备注"行业+需求"</span>*/}
                                            {/*        </div>*/}
                                            {/*    </div>*/}
                                            {/*    <img*/}
                                            {/*        src="https://service.static.chanjet.com/assets/sem/static/images/people_img.png"*/}
                                            {/*        alt="小人图案"*/}
                                            {/*        className="qrcode-decoration-img"*/}
                                            {/*    />*/}
                                            {/*</div>*/}
                                        </div>
                                    </div>
                                    <button
                                        className="submit-button"
                                        onClick={() => stepChange(2)}
                                        disabled={submitting} // 根据提交状态禁用按钮
                                    >
                                        {submitting ? '提交中...' : '提交您的企业信息，生成专属解决方案'}
                                    </button>
                                </>
                            )}

                            {/* 验证提示框 */}
                            {validationMessage && (
                                <div className="validation-message">
                                    {validationMessage}
                                </div>
                            )}
                            {step === 2 &&
                                (showEmptyResultLoading ? (
                                    <div className="text_center">
                                        <img
                                            src="https://service.static.chanjet.com/assets/sem/static/sem/image/zan.png"
                                            className="zan_img"
                                            alt="赞"
                                        />
                                        <p className="zan_text">
                                            正在为您匹配专属信息...
                                            <br/>请稍候，这可能需要一些时间
                                        </p>
                                        <div className="loading-section">
                                            <div className="spinner"></div>
                                            <p>匹配中...</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text_center">

                                        <img
                                            src="https://service.static.chanjet.com/assets/sem/static/sem/image/zan.png"
                                            className="zan_img"
                                            alt="赞"
                                        />
                                        <p className="zan_text">
                                            已匹配到您的专属信息
                                            <br/>为您生成<span className="red">专属解决方案和演示账套</span>
                                        </p>
                                        <div className="zan_btns">
                                            {resultData.uuid && (
                                                <button className="solution_btn" onClick={() => goSolution()}>
                                                    点击获取专属解决方案
                                                </button>
                                            )}
                                            {resultData.accountUrl && (
                                                <button className="account_btn"
                                                        onClick={() => goAccount()}>免费进入专属体验账套</button>
                                            )}
                                        </div>
                                        <button className="prev_step" onClick={() => stepChange(1)}>
                                            返回上一步
                                        </button>
                                        <div className="wl_erwei_ctn">
                                            <ul className="erwei_left_text">
                                                <li className="benefit-item bold">
                                                    <span className="red-text">扫码领取</span>您的定制方案
                                                </li>
                                                <li className="benefit-item bold">
                                                    行业顾问1对1为您解读
                                                </li>
                                            </ul>
                                            <ul className="erwei_right_text">
                                                <li className="benefit-item bold">
                                                    专家深度<span className="red-text">业务诊断</span>
                                                </li>
                                                <li className="benefit-item bold">
                                                <span className="red-text">同行案例</span>立即发您
                                                </li>
                                                <li className="benefit-item bold">
                                                    扫码立享<span className="red-text">专属优惠</span>
                                                </li>
                                                <li className="benefit-item bold">
                                                    <span className="red-text">960万+</span>老板都在用
                                                </li>
                                            </ul>
                                            <div id="wl_id"></div>
                                        </div>
                                    </div>
                                ))
                            }
                            {
                                step !== 2 && (
                                    <div className="trust-indicators">
                                        <div className="trust-item">
                                            <img
                                                src="https://service.static.chanjet.com/assets/sem/static/sem/image/red_check.png"
                                                className="check-icon"
                                            />
                                            <span>信息安全保护</span>
                                        </div>
                                        <div className="trust-item">
                                            <img
                                                src="https://service.static.chanjet.com/assets/sem/static/sem/image/red_check.png"
                                                className="check-icon"
                                            />
                                            <span>30s快速生成</span>
                                        </div>
                                        <div className="trust-item">
                                            <img
                                                src="https://service.static.chanjet.com/assets/sem/static/sem/image/red_check.png"
                                                className="check-icon"
                                            />
                                            <span>用友37年品牌保障</span>
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// 初始化弹窗根组件
const initPopup = () => {
    let container = document.createElement('div');
    container.id = 'popup_container';
    document.body.appendChild(container);

    const root = createRoot(container);
    root.render(<PopupRoot/>);
};

// 初始化弹窗组件
initPopup();

// 导出API函数
export const openPopup = (params: PopupParams = {}) => {
    if ((window as any).PopupAPI) {
        (window as any).PopupAPI.open(params);
    } else {
        console.error('Popup component not initialized');
    }
};

export const closePopup = () => {
    if ((window as any).PopupAPI) {
        (window as any).PopupAPI.close();
    } else {
        console.error('Popup component not initialized');
    }
};

export default {
    openPopup,
    closePopup
};
